import ImageSlot from '../shared/ImageSlot.jsx';
import Reveal from '../shared/Reveal.jsx';
import Tag from '../shared/Tag.jsx';
import { useParallax } from '../hooks/useParallax.js';

/*
 * One rung of the timeline. `side` is where the text card sits on desktop; the
 * photo takes the other column and the notched corner of the card points back
 * towards the spine. Below `lg` everything stacks photo-over-card, clear of the
 * left-gutter spine.
 */
export default function PathStage({ stage, last = false }) {
  const photoRef = useParallax(-0.04);
  const cardOnLeft = stage.side === 'left';

  const photo = (
    <Reveal className={cardOnLeft ? 'lg:order-3 lg:pl-2' : 'lg:order-1 lg:pr-2'}>
      <div
        ref={photoRef}
        className="aspect-[5/4] overflow-hidden rounded-[30px] border-4 border-white shadow-[0_14px_30px_rgb(20_83_45_/_0.14)]"
      >
        <ImageSlot src={stage.photo} alt={stage.photoAlt} />
      </div>
    </Reveal>
  );

  const card = (
    <Reveal
      index={1}
      className={cardOnLeft ? 'lg:order-1 lg:pr-2' : 'lg:order-3 lg:pl-2'}
    >
      <div
        className={`border-[3px] border-forest bg-white px-[30px] py-7 shadow-[0_10px_0_rgb(20_83_45_/_0.13)] ${
          cardOnLeft ? 'rounded-[30px_30px_44px_30px]' : 'rounded-[30px_30px_30px_44px]'
        }`}
      >
        <div className="flex items-center gap-2.5">
          <span
            className={`font-display text-[13px] font-extrabold uppercase tracking-[0.1em] ${stage.eyebrowClass}`}
          >
            {stage.eyebrow}
          </span>
          <span className="h-0.5 flex-1 bg-line" />
          <span className="text-[13px] font-bold text-ink-soft">{stage.ages}</span>
        </div>

        <h3 className="mt-2.5 font-display text-[34px] font-extrabold leading-[1.05] text-forest">
          {stage.title}
        </h3>
        <p className="mt-2.5 text-[16.5px] leading-[1.6] text-ink-soft">{stage.body}</p>

        <div className="mt-[18px] flex flex-wrap gap-2">
          {stage.tags.map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </div>

        <a
          href="#admissions"
          className="mt-5 inline-flex items-center gap-[7px] font-display text-[17px] font-bold text-forest transition-colors hover:text-coral"
        >
          {stage.linkLabel} →
        </a>
      </div>
    </Reveal>
  );

  return (
    <div
      className={`grid items-center gap-8 pl-12 lg:grid-cols-[1fr_92px_1fr] lg:gap-0 lg:pl-0 ${
        last ? 'pb-10' : 'pb-14 lg:pb-[76px]'
      }`}
    >
      {photo}
      <div className="hidden lg:order-2 lg:block" />
      {card}
    </div>
  );
}
